import React from "react";

// Presentation-only formatting for reportData.explanation.methodologyText
// (Section 2's "How is this scored" callout — sourced from backend
// content.howScored.content, see report-model.js). Never changes the
// underlying string, meaning, scoring, or classification — only how this
// one field is displayed:
//  1. normalizeEmDashList turns "lead-in — a, b, c — continuation" into
//     "lead-in: a, b, c, continuation" (colon for the first separator,
//     comma for the rest) — ordinary sentence punctuation instead of
//     em-dash-bracketed asides, same meaning. If the backend copy is ever
//     edited to drop the em dashes, this is a no-op (the split finds
//     nothing to rejoin and returns the text unchanged).
//  2. renderMethodologyText then bolds+colors each trait name it finds,
//     matched against this report's own localized factor titles
//     (reportData.factors[].title) rather than hardcoded English words, so
//     it keeps working in every locale.

export const normalizeEmDashList = (text) => {
  const segments = text.split(" — ");
  return segments.reduce(
    (result, segment, index) =>
      index === 0 ? segment : `${result}${index === 1 ? ": " : ", "}${segment}`,
    ""
  );
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Fixed per-trait "identity" color for naming a trait in body copy —
// independent of that trait's actual score, unlike getFactorVisualStyle's
// percentage-ramped colors (report-visual-style.js), which intentionally
// vary with percentage and wouldn't make sense for generic explanatory
// text. Same hue family the report already assigns per trait (report-
// model.js's FACTOR_COLORS: purple/red/teal) — meanness's identity hue is
// rendered here as Tailwind's rose-700, the closest named swatch to that
// same warm-red identity color.
const TRAIT_IDENTITY_CLASSES = {
  boldness: "text-purple-700",
  meanness: "text-rose-700",
  disinhibition: "text-teal-700",
};

export const renderMethodologyText = (text, factors) => {
  const normalized = normalizeEmDashList(text);
  const traits = factors.filter(
    (factor) => factor.title && TRAIT_IDENTITY_CLASSES[factor.key]
  );

  if (traits.length === 0) return normalized;

  const pattern = new RegExp(
    `(${traits.map((factor) => escapeRegExp(factor.title)).join("|")})`,
    "g"
  );

  return normalized.split(pattern).map((part, index) => {
    const trait = traits.find((factor) => factor.title === part);
    if (!trait) return part;
    return (
      <span
        key={`${trait.key}-${index}`}
        className={`font-semibold ${TRAIT_IDENTITY_CLASSES[trait.key]}`}
      >
        {part}
      </span>
    );
  });
};
